/**
 * Reporting Period Bar - Page-level component
 * Displays and controls the reporting period selector with presets and custom range
 * Shared across all owner pages
 */
import { useState } from 'react';
import { useReportingPeriod } from '../../contexts/ReportingPeriodContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';

const PRESETS = [
  { id: 'current-month', label: 'Current Month' },
  { id: 'previous-month', label: 'Previous Month' },
  { id: 'past-3-months', label: 'Last 3 Months' },
  { id: 'past-6-months', label: 'Last 6 Months' },
  { id: 'past-12-months', label: 'Last 12 Months' },
  { id: 'next-3-months', label: 'Next 3 Months' },
  { id: 'next-6-months', label: 'Next 6 Months' },
  { id: 'this-year', label: 'This Year (Jan - Dec)' },
  { id: 'next-year', label: 'Next Year' },
  { id: 'last-year', label: 'Last Year' },
  { id: 'custom', label: 'Custom Range...' },
];

export default function ReportingPeriodBar({ labelFontSize, onAfterChange } = {}) {
  const { presetId, rangeStart, rangeEnd, getResolvedLabel, getResolvedSpanText, setPreset, setCustomRange } = useReportingPeriod();
  const { theme } = useTheme();
  const labelStyle = { ...styles.label, ...(labelFontSize ? { fontSize: labelFontSize } : {}) };
  const selectStyle = { ...styles.select, ...(labelFontSize ? { fontSize: labelFontSize } : {}) };
  const [showCustom, setShowCustom] = useState(presetId === 'custom');
  const [customStartInput, setCustomStartInput] = useState(
    rangeStart ? `${rangeStart.getFullYear()}-${String(rangeStart.getMonth() + 1).padStart(2, '0')}-${String(rangeStart.getDate()).padStart(2, '0')}` : ''
  );
  const [customEndInput, setCustomEndInput] = useState(
    rangeEnd ? `${rangeEnd.getFullYear()}-${String(rangeEnd.getMonth() + 1).padStart(2, '0')}-${String(rangeEnd.getDate()).padStart(2, '0')}` : ''
  );

  const handlePresetChange = (e) => {
    const newPresetId = e.target.value;
    setPreset(newPresetId);
    if (newPresetId === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      onAfterChange?.();
    }
  };

  const handleCustomApply = () => {
    if (customStartInput && customEndInput) {
      // Parse dates as local dates (no timezone conversion)
      // customStartInput and customEndInput are in YYYY-MM-DD format
      const [startYear, startMonth, startDay] = customStartInput.split('-').map(Number);
      const [endYear, endMonth, endDay] = customEndInput.split('-').map(Number);
      
      // Create dates at local midnight (no timezone shift)
      const start = new Date(startYear, startMonth - 1, startDay);
      // For end date, backend expects end-exclusive, so we add 1 day to make it exclusive
      // e.g., if user selects Oct 31, we want Oct 1 to Nov 1 (end-exclusive) = Oct 1-31 inclusive
      const end = new Date(endYear, endMonth - 1, endDay + 1);
      
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
        setCustomRange(start, end);
        setShowCustom(false);
        onAfterChange?.();
      }
    }
  };

  return (
    <div style={{
      ...styles.container,
      borderBottomColor: theme === 'dark' ? 'var(--color-border-light-dark)' : 'var(--color-border-light)',
    }}>
      <div className="reporting-period-content" style={styles.content}>
        <label style={{
          ...labelStyle,
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
        }} htmlFor="reporting-period-preset">
          Reporting Period:
        </label>
        <select
          id="reporting-period-preset"
          value={presetId}
          onChange={handlePresetChange}
          style={{
            ...selectStyle,
            backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
            color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
            borderColor: theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)',
          }}
          aria-label="Select reporting period preset"
        >
          {PRESETS.map(preset => (
            <option key={preset.id} value={preset.id}>{preset.label}</option>
          ))}
        </select>

        {showCustom && (
          <div style={{...styles.customRange, flexWrap: 'nowrap', minWidth: 0, display: 'inline-flex', alignItems: 'center'}}>
            <input
              type="date"
              value={customStartInput}
              onChange={(e) => setCustomStartInput(e.target.value)}
              style={{
                ...styles.dateInput,
                backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
                color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                borderColor: theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)',
              }}
              aria-label="Custom range start date"
            />
            <span style={styles.dateSeparator}>to</span>
            <input
              type="date"
              value={customEndInput}
              onChange={(e) => setCustomEndInput(e.target.value)}
              style={{
                ...styles.dateInput,
                backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
                color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                borderColor: theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)',
              }}
              aria-label="Custom range end date"
            />
            <button
              onClick={handleCustomApply}
              style={{
                ...styles.applyButton,
                backgroundColor: theme === 'dark' ? 'var(--color-primary-base)' : 'var(--color-primary-base)',
                color: 'var(--color-text-inverse)',
              }}
            >
              Apply
            </button>
          </div>
        )}

        {/* Date range display removed - shown in AnalyticsHeader instead to avoid duplication */}
      </div>
    </div>
  );
}

const styles = {
  container: {
    marginBottom: 0, /* No margin when used in ContextBar */
    paddingBottom: 0, /* No padding when used in ContextBar */
    borderBottom: 'none', /* No border when used in ContextBar */
    display: 'flex', // Make container flex to align with sibling
    alignItems: 'center', // Align with "Showing:" text
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)',
    flexWrap: 'wrap',
    minWidth: '0', // Prevent overflow
    maxWidth: '100%', // Constrain to parent
  },
  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap',
  },
  select: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border-medium)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    height: '36px',
    minWidth: '180px',
  },
  customRange: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    flexWrap: 'wrap',
    minWidth: '0', // Prevent overflow
  },
  dateInput: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border-medium)',
    fontSize: 'var(--font-size-sm)',
    height: '36px',
    minWidth: '140px',
  },
  dateSeparator: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
  applyButton: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    height: '36px',
    whiteSpace: 'nowrap',
  },
  rangeDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    marginLeft: 'var(--spacing-2)',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  rangeLabel: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
  },
  rangeText: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
};

